import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          body.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9rcmvfnahb7382ovki0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
          || (parsed?.message as string)
          || responseText;
        setStatus('error');
        setErrorMessage(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setErrorMessage('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <section className="w-full py-14 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
            联系我们
          </h2>
          <p className="text-sm md:text-base text-foreground-600">
            如有任何问题或建议，请填写以下表单，我们的客服团队将在24小时内回复
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form
          className="flex flex-col gap-5"
        >
          <div className="form-honeypot">
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="name" className="text-sm font-medium text-foreground-700">
                姓名 <span className="text-primary-500">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                className="px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                placeholder="请输入您的姓名"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-sm font-medium text-foreground-700">
                邮箱 <span className="text-primary-500">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                placeholder="请输入您的邮箱地址"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="subject" className="text-sm font-medium text-foreground-700">
              主题 <span className="text-primary-500">*</span>
            </label>
            <input
              id="subject"
              name="subject"
              type="text"
              required
              className="px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
              placeholder="请输入咨询主题"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="message" className="text-sm font-medium text-foreground-700">
              留言内容 <span className="text-primary-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              maxLength={500}
              rows={5}
              className="px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
              placeholder="请详细描述您的问题或建议（500字以内）"
            />
          </div>

          {status === 'success' && (
            <div className="p-4 rounded-md bg-accent-100/80 text-accent-800 text-sm">
              感谢您的留言！我们已收到您的信息，客服团队将在24小时内通过邮件回复您。
            </div>
          )}

          {status === 'error' && (
            <div className="p-4 rounded-md bg-primary-100 text-primary-800 text-sm">
              {errorMessage || '提交失败，请稍后重试'}
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'loading'}
            className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap self-start"
          >
            {status === 'loading' ? '提交中...' : '提交留言'}
          </button>
        </form>

        <div className="mt-12 pt-8 border-t border-background-200 grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
              <i className="ri-mail-line text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</p>
              <p className="text-sm text-foreground-600">support@jmtthub.com</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
              <i className="ri-time-line text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-foreground-800 mb-1">工作时间</p>
              <p className="text-sm text-foreground-600">周一至周日 9:00 - 21:00</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
              <i className="ri-question-answer-line text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-foreground-800 mb-1">响应时间</p>
              <p className="text-sm text-foreground-600">24小时内回复</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}